import React from "react";
import { Link } from "react-router-dom";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/services";
import { Overline } from "@/components/brand/SectionDivider";
import { ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/brand";
import { AmbientLight } from "@/components/brand/AmbientLight";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";
import useDocumentTitle from "@/lib/useDocumentTitle";

export default function ClaimServicesHub() {
  useDocumentTitle("Claim Services | AG Public Adjusting");
  return (
    <main data-testid="services-hub-page" className="pt-32 bg-midnight-gradient overflow-hidden">
      <section className="relative py-20 lg:py-28">
        <AmbientLight size={1100} color="blue" intensity={0.45} style={{ top: "-5%", left: "20%" }} />
        <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" aria-hidden>
          <ConcentricMonogram size={500} variant="ghost" glowColor="blue" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
          <Overline className="mb-8">Claim Services</Overline>
          <h1 className="font-display-tight text-white text-[clamp(2.8rem,6vw,6rem)] leading-[0.95] tracking-[-0.045em] max-w-4xl">
            Fifteen categories.
            <br />
            <span className="gold-metallic font-display font-light">One disciplined approach.</span>
          </h1>
          <p className="mt-10 text-ag-text-secondary text-lg leading-[1.65] font-light max-w-2xl">
            AG Public Adjusting represents Florida policyholders across the full spectrum of property
            insurance claims — residential, commercial, condominium, and claim recovery.
          </p>
        </div>
      </section>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-16 pb-24 lg:pb-32">
        <div className="space-y-20">
          {SERVICE_CATEGORIES.map((cat) => {
            const services = SERVICES.filter((s) => s.category === cat.id);
            return (
              <section key={cat.id} data-testid={`services-category-${cat.id}`}>
                <div className="flex items-end justify-between mb-8 border-b border-white/10 pb-6">
                  <div>
                    <Overline>{cat.label}</Overline>
                    <h2 className="font-display-tight text-white text-[clamp(1.8rem,3vw,2.5rem)] mt-4 leading-[1.05] tracking-[-0.035em] font-light max-w-2xl">{cat.description}</h2>
                  </div>
                  <span className="font-mono text-[0.65rem] text-ag-text-muted tracking-[0.22em] hidden md:block">{String(services.length).padStart(2, "0")} SERVICES</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/claim-services/${s.slug}`}
                      data-testid={`hub-service-${s.slug}`}
                      className="glass block p-8 transition-all duration-500 group relative overflow-hidden hover:border-gold/30"
                    >
                      <img
                        src={BRAND.logoWhite}
                        alt=""
                        aria-hidden="true"
                        className="absolute -right-6 -bottom-6 h-28 w-auto object-contain opacity-0 group-hover:opacity-[0.06] transition-opacity duration-700"
                      />
                      <h3 className="font-display text-xl text-white group-hover:gold-metallic group-hover:text-transparent transition-colors mb-3 font-light tracking-[-0.02em]">
                        {s.title}
                      </h3>
                      <p className="text-sm text-ag-text-secondary leading-[1.65] font-light line-clamp-3">{s.short}</p>
                      <div className="mt-6 inline-flex items-center gap-2 font-mono text-[0.65rem] tracking-[0.22em] uppercase text-gold/70">
                        Explore <ArrowUpRight className="w-3 h-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </main>
  );
}
